import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {roomSelector, roomUserCountSelector} from '../store/selectors/roomRunFile.selectors';
import {loadedPresentationSelector} from '../store/selectors/presentation.selectors';
import {IElementQuestionnaire, IElementQuestionnaireWithAnswers} from '../incase-domain/elements/base/questionElementBase';
import {QuestionElementInteractions} from '../incase-domain/proptypes/runfile';
import {getAttemptsAndRetriable} from '../incase-domain/utils/retryCount';
import {DEFAULT_NUMBER_OF_RETRIES} from '../constants';
import {ELEMENT} from '../incase-domain/elements/base/ElementTypes';
import {useUserQuestionResponseCount} from './useUserQuestionResponseCount';
import {useQuestionRemaining} from '../components/element/types/question/common/useQuestionRemaining';
import {AnswerTypes} from '../components/element/types/question/question/QuestionAnswer';

export interface QuestionCounterProps {
    numerator: number;
    denominator: number;
    hidden: boolean;
    getLabel: () => string;
    showCorrectAnswersCount: boolean;
    correctAnswersCount: number;
}

export function useQuestionCounterProps(
    element: IElementQuestionnaire,
    elementInteractions: QuestionElementInteractions
): QuestionCounterProps {
    const {t} = useTranslation();
    const {isPresenter} = useSelector(roomSelector);
    const presentationMeta = useSelector(loadedPresentationSelector);
    const attemptsAndRetriable = getAttemptsAndRetriable(element, presentationMeta);
    const {remainingAttempts} = useQuestionRemaining(element, elementInteractions);
    const count = useUserQuestionResponseCount(element.id);
    // userCount: admin is excluded from the live count but can also submit answers
    const userCount = useSelector(roomUserCountSelector);

    const numerator = isPresenter ? count : remainingAttempts;
    const denominator = isPresenter ? userCount : attemptsAndRetriable?.maxAttempts || DEFAULT_NUMBER_OF_RETRIES;

    const isSingleAttemptType =
        element.type === ELEMENT.YES_NO_QUESTION || element.type === ELEMENT.YES_NO_QUESTION_V2 || element.type === ELEMENT.LIKERT_SCALE;

    const hidden = (!attemptsAndRetriable?.retriable && !isPresenter) || (isSingleAttemptType && !isPresenter);

    const getLabel = (): string => {
        if (isPresenter) return `${numerator} / ${denominator} ${t('slide.questionCounter.answers')}`;
        if (attemptsAndRetriable?.retriable) return `${numerator} ${t('slide.questionCounter.attemptsLeft')}`;
        return t('slide.questionCounter.singleAttempt');
    };

    const showCorrectAnswersCount = !!element.showCorrectAnswersCount;
    const answers = 'answers' in element ? (element as IElementQuestionnaireWithAnswers).answers : undefined;
    const correctAnswersCount =
        answers?.reduce((prev, curr) => {
            const isCorrect = curr.type === AnswerTypes.CORRECT;
            if (isCorrect) {
                return prev + 1;
            }

            return prev;
        }, 0) ?? 0;

    return {numerator, denominator, hidden, getLabel, showCorrectAnswersCount, correctAnswersCount};
}
